import { useEffect, useState, useRef } from "react";
import { Star, ImagePlus, X } from "lucide-react";
import { api, BACKEND_URL, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "./Reveal";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

const fullUrl = (p) => (p ? `${BACKEND_URL}${p}` : null);

export function Reviews() {
  const { universe } = useTheme();
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [photoPath, setPhotoPath] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 10);
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reviews/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.photo_path);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPreview(null);
    } finally { setUploading(false); }
  };

  const removePhoto = () => { setPhotoPath(null); setPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { ...form, photo_path: photoPath });
      toast.success("Merci ! Votre avis sera publié après validation.");
      setForm({ name: "", rating: 5, text: "" });
      removePhoto();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="py-20 md:py-32 border-t" style={{ borderColor: "var(--lo-border)" }} data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="overline text-center mb-4">Témoignages</p>
          <h2 className="font-display text-4xl sm:text-5xl text-center mb-4" style={{ fontWeight: 300 }}>{universe === "homme" ? "Ils" : "Elles"} parlent de leur rituel</h2>
          <div className="gold-line w-24 mx-auto mb-16" />
        </Reveal>

        {reviews.length === 0 ? (
          <Reveal>
            <p className="text-center text-sm max-w-md mx-auto mb-16" style={{ color: "var(--lo-muted)" }}>
              Les premiers avis authentiques de nos clientes apparaîtront ici. Soyez parmi les premières à partager votre expérience.
            </p>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {visibleReviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <div className="card-lux p-8 h-full" data-testid="review-card">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} size={14} fill={j < r.rating ? "var(--lo-rose)" : "none"} stroke="var(--lo-rose)" />
                    ))}
                  </div>
                  {r.photo_url && <img src={fullUrl(r.photo_url)} alt="Avis client" loading="lazy" decoding="async" className="w-full h-44 object-cover mb-4 cursor-zoom-in transition-transform duration-300 hover:scale-[1.01]" style={{ border: "1px solid var(--lo-border)" }} data-testid="review-photo" onClick={() => setSelectedPhoto(fullUrl(r.photo_url))} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedPhoto(fullUrl(r.photo_url)); } }} />}
                  <p className="font-display text-xl leading-snug mb-4" style={{ fontWeight: 300 }}>&laquo; {r.text} &raquo;</p>
                  <p className="overline" style={{ fontSize: "0.6rem" }}>{r.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {reviews.length > 10 && (
          <Reveal>
            <div className="flex justify-center mb-16">
              <button
                type="button"
                onClick={() => setShowAllReviews((v) => !v)}
                className="btn-ghost h-12 px-8"
                data-testid="reviews-toggle"
              >
                {showAllReviews ? "Afficher moins d’avis" : "Afficher plus d’avis"}
              </button>
            </div>
          </Reveal>
        )}

        {selectedPhoto && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(0,0,0,0.92)" }}
            role="dialog"
            aria-modal="true"
            aria-label="Photo de l'avis client"
            onClick={() => setSelectedPhoto(null)}
          >
            <button
              type="button"
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full flex items-center justify-center z-10"
              style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)" }}
              aria-label="Fermer la photo"
              data-testid="review-photo-modal-close"
            >
              <X size={22} />
            </button>
            <img
              src={selectedPhoto}
              alt="Photo agrandie de l'avis client"
              className="max-w-full max-h-[90vh] object-contain rounded-sm"
              style={{ border: "1px solid var(--lo-border)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="review-photo-modal"
            />
          </div>
        )}

        <Reveal>
          <form onSubmit={submit} className="max-w-xl mx-auto card-lux p-8" data-testid="review-form">
            <p className="overline mb-6">Laisser un avis</p>
            <input required placeholder="Votre prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b py-3 mb-5 outline-none text-sm" style={{ borderColor: "var(--lo-border)" }} data-testid="review-name" />
            <div className="flex gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                  <Star size={22} fill={n <= form.rating ? "var(--lo-rose)" : "none"} stroke="var(--lo-rose)" />
                </button>
              ))}
            </div>
            <textarea required placeholder="Partagez votre expérience..." value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} className="w-full bg-transparent border-b py-3 mb-5 outline-none text-sm resize-none" style={{ borderColor: "var(--lo-border)" }} data-testid="review-text" />

            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="review-photo-input" />
            {preview ? (
              <div className="relative inline-block mb-6">
                <img src={preview} alt="Aperçu" className="w-24 h-24 object-cover" style={{ border: "1px solid var(--lo-border-rose)" }} />
                <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "var(--lo-rose)", color: "#0A0505" }} data-testid="review-photo-remove"><X size={13} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost h-11 px-5 mb-6 inline-flex items-center gap-2" data-testid="review-photo-btn">
                <ImagePlus size={15} /> {uploading ? "Envoi..." : "Ajouter une photo"}
              </button>
            )}

            <button disabled={busy || uploading} className="btn-primary h-12 px-8 w-full disabled:opacity-50" data-testid="review-submit">{busy ? "Envoi..." : "Envoyer mon avis"}</button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
